import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, message, Popconfirm, Row } from "antd";
import { ColumnsType } from "antd/lib/table";
import { FC } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import CheckboxSlider from "../../../components/SliderList/CheckboxSlider";
import TableWrap from "../../../components/Table";
import TopContent from "../../../components/TopContent";
import PageWrap from "../../../layout/PageWrap";
import { messages } from "../../../utils/messages";

export interface LandingListConfig {
  title: string;
  listPath: string;
  addPath: string;
  queryKey: string;
  getList: () => Promise<any>;
  editItem: (args: { id: string | undefined; data: any }) => Promise<any>;
  deleteItem: (id: string) => Promise<any>;
  titleField: string;
  subtitleField?: string;
}

const LandingList: FC<{ config: LandingListConfig }> = ({ config }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching }: any = useQuery(
    [config.queryKey],
    config.getList
  );

  const mutation = useMutation(config.deleteItem, {
    onSuccess: (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.deleteSuccess);
        queryClient.invalidateQueries([config.queryKey]);
      } else {
        message.error(res?.data?.message ?? messages.error);
      }
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const mutationEdit = useMutation(config.editItem, {
    onSuccess: (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.updateSuccess);
        queryClient.invalidateQueries([config.queryKey]);
      } else {
        message.error(res?.data?.message ?? messages.error);
      }
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const columns: ColumnsType<any> = [
    {
      title: "STT",
      align: "center",
      width: "5%",
      dataIndex: "stt",
      key: "stt",
    },
    {
      title: "Nội dung",
      render: (_, record: any) => (
        <>
          <b>{record?.[config.titleField]}</b>
          {config.subtitleField ? (
            <>
              <br />
              {record?.[config.subtitleField]}
            </>
          ) : null}
        </>
      ),
    },
    {
      title: "Hiển thị",
      dataIndex: "hienthi",
      align: "center",
      width: "8%",
      render: (record: boolean, row: any) => (
        <CheckboxSlider
          _id={row._id}
          check={record}
          dataName="hienthi"
          action={mutationEdit}
        />
      ),
    },
    {
      title: "Sửa",
      align: "center",
      width: "5%",
      render: (_, record: any) => (
        <EditOutlined
          onClick={() => navigate(`${config.listPath}/${record?._id}`)}
        />
      ),
    },
    {
      title: "Xóa",
      align: "center",
      width: "5%",
      render: (_, record: any) => (
        <Popconfirm
          title="Xác nhận xóa mục này"
          onConfirm={() => mutation.mutate(record._id)}
        >
          <DeleteOutlined style={{ color: "red" }} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <PageWrap
      title={config.title}
      content={
        <>
          <TopContent
            content={
              <Row>
                <Col>
                  <b>{config.title}</b>
                </Col>
                <Col>
                  <Button onClick={() => navigate(config.addPath)}>
                    <PlusOutlined /> Add
                  </Button>
                </Col>
              </Row>
            }
          />
          <div className="content">
            <TableWrap
              loading={isLoading || isFetching}
              columns={columns}
              isShowTotal
              dataSource={data?.data}
              rowKey="_id"
              bordered
              pagination={false}
              className="hover table-slide"
              hasDefaultColumn={false}
            />
          </div>
        </>
      }
    />
  );
};

export default LandingList;
